/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import type { TaskModifierType } from '@kaiizen/kai-pro-api';

import { styleguideColors as COLORS, styleguideFontSize as FONT_SIZE, styleguideFontWeight as FONT_WEIGHT } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { useTooltip } from '@/Client/Widgets';
import { SettingsToggle } from '@/Client/Widgets/Settings';
import { styleguideFontFamily as FONT } from '@kaiizen/kai-shiki/core';
import { CheckIcon, CloseIcon, InfoCircleIcon, LockIcon } from '@kaiizen/kai-shiki/react';

import {
  MODIFIER_FIELD_USER_COPY,
  MODIFIER_LABEL_MAX_LENGTH,
  MODIFIER_LABEL_TIP,
  MODIFIER_SETTINGS_NOTE,
  MODIFIER_TYPE_COPY,
} from './modifierTypeCopy';

const LABEL_FIELD_STYLE = css`
  label: MODIFIER_LABEL_FIELD;
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 20px;
  padding: 0 14px 12px;
  max-width: 600px;
  border: 1px solid ${COLORS.stroke};
`;

const LABEL_LEGEND_STYLE = css`
  label: MODIFIER_LABEL_LEGEND;
  padding: 0 5px;
  margin-left: -5px;
  font-family: ${FONT.body};
  font-size: ${pxToRem(FONT_SIZE.bodyMicro)};
  color: ${COLORS.midtone};
`;

const LABEL_INPUT_STYLE = css`
  label: MODIFIER_LABEL_INPUT;
  flex: 1;
  min-width: 0;
  border: 0;
  background: none;
  font: inherit;
  font-size: ${pxToRem(FONT_SIZE.bodyInput)};

  &:focus-visible {
    outline: none;
  }
`;

const LABEL_COUNT_STYLE = css`
  label: MODIFIER_LABEL_COUNT;
  font-family: ${FONT.body};
  font-size: ${pxToRem(FONT_SIZE.bodyMicro)};
  color: ${COLORS.midtone};
  font-variant-numeric: tabular-nums;
`;

/* Mockup `.info`: a bordered circle carrying the 2026 tooltip, not a glyph with a native
   `title=` — which gave no styling, no delay and no keyboard path. */
const INFO_STYLE = css`
  label: MODIFIER_INFO;
  width: 17px;
  height: 17px;
  flex-shrink: 0;
  padding: 0;
  border: 1.2px solid ${COLORS.midtone};
  border-radius: 50%;
  background: none;
  color: ${COLORS.midtone};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: default;

  svg {
    width: 11px;
    height: 11px;
  }

  &:hover,
  &:focus-visible {
    border-color: ${COLORS.ink};
    color: ${COLORS.ink};
  }
`;

const BLOCK_HEADER_STYLE = css`
  label: MODIFIER_RULES_HEADER;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding-bottom: 12px;

  svg {
    width: 15px;
    height: 15px;
    flex-shrink: 0;
    margin-top: 1px;
    color: ${COLORS.midtone};
  }
`;

const BLOCK_TITLE_STYLE = css`
  label: MODIFIER_RULES_TITLE;
  margin: 0 0 1px;
  font-size: ${pxToRem(FONT_SIZE.bodyMedium)};
  font-weight: ${FONT_WEIGHT.bold};
`;

const BLOCK_BODY_STYLE = css`
  label: MODIFIER_RULES_BODY;
  margin: 1px 0 0;
  font-size: ${pxToRem(FONT_SIZE.bodySmall)};
  line-height: 1.45;
  color: ${COLORS.midtone};
`;

/* Mockup `.rows`: the rules are one bordered group, and each row is inset within it. They had
   been flush to the card edge with dividers only, which is the interim settings look. */
const ROWS_STYLE = css`
  label: MODIFIER_RULE_ROWS;
  border: 1px solid ${COLORS.stroke};
`;

const ROWS_STANDALONE_STYLE = css`
  label: MODIFIER_RULE_ROWS_STANDALONE;
  margin-top: 14px;
`;

/** The Settings group butts against the block header above it, so it drops its own top edge. */
const ROWS_SETTINGS_STYLE = css`
  label: MODIFIER_RULE_ROWS_SETTINGS;
  border-top: 0;
`;

const ROW_STYLE = css`
  label: MODIFIER_RULE_ROW;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
  padding: 14px 18px;
  border-bottom: 1px solid ${COLORS.stroke};

  &:last-of-type {
    border-bottom: 0;
  }
`;

/** Mockup `.row-off`: an Off rule recedes rather than reading as an available choice. */
const ROW_OFF_STYLE = css`
  label: MODIFIER_RULE_ROW_OFF;
  background: ${COLORS.paper};
  opacity: 0.6;

  h5 {
    font-weight: ${FONT_WEIGHT.regular};
  }
`;

const ROW_TITLE_STYLE = css`
  label: MODIFIER_RULE_TITLE;
  margin: 0;
  font-size: ${pxToRem(FONT_SIZE.bodyMedium)};
  font-weight: ${FONT_WEIGHT.bold};
`;

const ROW_BODY_STYLE = css`
  label: MODIFIER_RULE_BODY;
  margin: 2px 0 0;
  font-size: ${pxToRem(FONT_SIZE.bodySmall)};
  line-height: 1.45;
  color: ${COLORS.midtone};
  max-width: 820px;
`;

/**
 * Mockup `.state`: a glyph and a word, with no button, border or fill — the rules are Kai's and
 * an admin cannot change them, so nothing here may look pressable.
 */
const STATE_STYLE = css`
  label: MODIFIER_RULE_STATE;
  margin-left: auto;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: ${FONT.body};
  font-size: ${pxToRem(FONT_SIZE.bodyMedium)};
  font-weight: ${FONT_WEIGHT.medium};
  color: ${COLORS.ink};
  white-space: nowrap;

  svg {
    width: 14px;
    height: 14px;
  }
`;

const STATE_OFF_STYLE = css`
  label: MODIFIER_RULE_STATE_OFF;
  color: ${COLORS.midtone};
`;

/** Mockup `.settings-hdr`: the same filled bar, between the rules box and the settings box. */
const SETTINGS_HEADER_STYLE = css`
  label: MODIFIER_SETTINGS_HEADER;
  margin-top: 18px;
  padding: 13px 18px;
  background: ${COLORS.shell};
  border: 1px solid ${COLORS.stroke};
  border-top: 0;
  border-bottom: 0;
`;

/**
 * The expanded half of a modifier card: its label, the rules Kai sets for its type, and the one
 * setting an org actually controls.
 *
 * Financial rules are read-only by requirement — they are displayed as a glyph plus a word, never
 * as a control, so no admin can be led to think they are editable.
 */
export const ModifierCardBody = ({
  type,
  label,
  onLabelChange,
  onLabelBlur,
  onLabelFocus,
  fieldUsersEnabled,
  onToggleFieldUsers,
}: {
  type: TaskModifierType;
  label: string;
  onLabelChange: (label: string) => void;
  onLabelBlur?: () => void;
  onLabelFocus?: () => void;
  fieldUsersEnabled: boolean;
  onToggleFieldUsers: (enabled: boolean) => void;
}) => {
  const copy = MODIFIER_TYPE_COPY[type];
  const labelTooltip = useTooltip(MODIFIER_LABEL_TIP);

  return (
    <>
      <fieldset css={LABEL_FIELD_STYLE}>
        <legend css={LABEL_LEGEND_STYLE}>Modifier Label</legend>
        <input
          aria-label='Modifier Label'
          css={LABEL_INPUT_STYLE}
          maxLength={MODIFIER_LABEL_MAX_LENGTH}
          onBlur={onLabelBlur}
          onFocus={onLabelFocus}
          onChange={event => onLabelChange(event.target.value)}
          type='text'
          value={label}
        />
        <span css={LABEL_COUNT_STYLE}>{`${label.length}/${MODIFIER_LABEL_MAX_LENGTH}`}</span>
        <button
          aria-label='About the modifier label'
          css={INFO_STYLE}
          type='button'
          {...labelTooltip.anchorProps}
        >
          <InfoCircleIcon />
        </button>
        {labelTooltip.tooltip}
      </fieldset>

      <div css={BLOCK_HEADER_STYLE}>
        {/* Display has no lock: there are no rules for Kai to have set. */}
        {copy.rules ? <LockIcon aria-hidden='true' /> : null}
        <div>
          <h4 css={BLOCK_TITLE_STYLE}>{copy.rules ? 'Financial Rules' : copy.lock}</h4>
          <p css={BLOCK_BODY_STYLE}>{copy.rules ? copy.lock : copy.lockSub}</p>
        </div>
      </div>

      {copy.rules
        ? (
          <div css={ROWS_STYLE}>
            {copy.rules.map(rule => (
              <div css={[ROW_STYLE, !rule.on && ROW_OFF_STYLE]} key={rule.title}>
                <div>
                  <h5 css={ROW_TITLE_STYLE}>{rule.title}</h5>
                  <p css={ROW_BODY_STYLE}>{rule.description}</p>
                </div>
                <span
                  css={[STATE_STYLE, !rule.on && STATE_OFF_STYLE]}
                  data-testid={`modifier-rule-${rule.on ? 'on' : 'off'}`}
                >
                  {rule.on ? <CheckIcon aria-hidden='true' /> : <CloseIcon aria-hidden='true' />}
                  {rule.on ? 'On' : 'Off'}
                </span>
              </div>
            ))}
          </div>
        )
        : null}

      {/* The recommendation belongs to the financial types, which carry procurement consequences. */}
      {copy.rules
        ? (
          <div css={SETTINGS_HEADER_STYLE}>
            <h4 css={BLOCK_TITLE_STYLE}>Settings</h4>
            <p css={BLOCK_BODY_STYLE}>{MODIFIER_SETTINGS_NOTE}</p>
          </div>
        )
        : null}

      {
        /* The field-user setting is its own bordered group: it butts against the Settings header
           for the financial types, and stands alone for Display, which has no header above it. */
      }
      <div css={[ROWS_STYLE, copy.rules ? ROWS_SETTINGS_STYLE : ROWS_STANDALONE_STYLE]}>
        <div css={ROW_STYLE}>
          <div>
            <h5 css={ROW_TITLE_STYLE}>Allow field users to apply</h5>
            <p css={ROW_BODY_STYLE}>{MODIFIER_FIELD_USER_COPY}</p>
          </div>
          <span css={STATE_STYLE}>
            <SettingsToggle
              aria-label='Allow field users to apply'
              checked={fieldUsersEnabled}
              onClick={() => onToggleFieldUsers(!fieldUsersEnabled)}
            />
          </span>
        </div>
      </div>
    </>
  );
};
